import { createSlice, createAsyncThunk } from "@reduxjs/toolkit"
import { get } from "../../lib/request"
import {
  appendOrganizationBrandIds,
  appendOrganizationClientIds,
  organizationClientIdsKey,
} from "../../lib/utils/organizationReportClientIds"

function pageEngagementRateKey({ organizationId, startDate, endDate, clientIds, brandIds } = {}) {
  return `${String(organizationId ?? "")}|${String(startDate ?? "")}|${String(endDate ?? "")}|${organizationClientIdsKey(clientIds)}|${organizationClientIdsKey(brandIds)}`
}

/**
 * GET /api/v1/report/organizations/{organizationId}/page/engagement-rate?start_date=&end_date=&client_ids=&brand_ids=
 */
export const fetchOrganizationPageEngagementRate = createAsyncThunk(
  "organizationPageEngagementRate/fetch",
  async ({ organizationId, startDate, endDate, clientIds, brandIds } = {}, { rejectWithValue }) => {
    try {
      if (organizationId == null || organizationId === "") {
        return rejectWithValue("organizationId is required")
      }
      if (!startDate || !endDate) {
        return rejectWithValue("start_date and end_date are required")
      }

      const query = new URLSearchParams({
        start_date: startDate,
        end_date: endDate,
      })
      appendOrganizationClientIds(query, clientIds)
      appendOrganizationBrandIds(query, brandIds)
      const data = await get(
        `report/organizations/${organizationId}/page/engagement-rate?${query}`,
      )

      return {
        data,
        organizationId,
        startDate,
        endDate,
        fetchKey: pageEngagementRateKey({ organizationId, startDate, endDate, clientIds, brandIds }),
      }
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to fetch organization engagement rate")
    }
  },
  {
    condition: (arg, { getState }) => {
      if (arg?.force) return true
      const key = pageEngagementRateKey(arg)
      const state = getState().organizationPageEngagementRate
      if (state?.status === "loading" && state?.requestedFetchKey === key) return false
      if (state?.status === "succeeded" && state?.fetchedFetchKey === key) return false
      return true
    },
  },
)

const initialState = {
  data: null,
  status: "idle",
  error: null,
  organizationId: null,
  startDate: null,
  endDate: null,
  requestedFetchKey: null,
  fetchedFetchKey: null,
}

const organizationPageEngagementRateSlice = createSlice({
  name: "organizationPageEngagementRate",
  initialState,
  reducers: {
    clearOrganizationPageEngagementRate: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchOrganizationPageEngagementRate.pending, (state, action) => {
        const arg = action.meta.arg || {}
        state.status = "loading"
        state.error = null
        state.requestedFetchKey = pageEngagementRateKey(arg)
      })
      .addCase(fetchOrganizationPageEngagementRate.fulfilled, (state, action) => {
        if (action.payload?.fetchKey !== state.requestedFetchKey) return
        state.status = "succeeded"
        state.data = action.payload?.data ?? null
        state.organizationId = action.payload?.organizationId ?? null
        state.startDate = action.payload?.startDate ?? null
        state.endDate = action.payload?.endDate ?? null
        state.fetchedFetchKey = action.payload?.fetchKey ?? null
      })
      .addCase(fetchOrganizationPageEngagementRate.rejected, (state, action) => {
        if (pageEngagementRateKey(action.meta.arg || {}) !== String(state.requestedFetchKey ?? "")) {
          return
        }
        state.status = "failed"
        state.error = action.payload ?? "Failed to fetch organization engagement rate"
        state.data = null
        state.fetchedFetchKey = state.requestedFetchKey
      })
  },
})

export const { clearOrganizationPageEngagementRate } = organizationPageEngagementRateSlice.actions
export default organizationPageEngagementRateSlice.reducer
